import type { ParticleTopology } from './particle-topology'

export interface SimulationInput {
  x: number
  y: number
  force: number
  speed: number
  active: boolean
}

export interface SimulatedNode {
  x: number
  y: number
  velocityX: number
  velocityY: number
}

export interface Spore {
  x: number
  y: number
  velocityX: number
  velocityY: number
  ageMs: number
  lifeMs: number
}

export interface ParticleSimulationState {
  nodes: SimulatedNode[]
  offsetX: number
  offsetY: number
  offsetVelocityX: number
  offsetVelocityY: number
  contraction: number
  gentleDwellMs: number
  spores: Spore[]
}

const MAX_RETREAT = 0.15
const POINTER_REACH = 0.6
const FAST_SPEED = 0.55
const GENTLE_SPEED = 0.08
const MAX_CONTRACTION = 0.18
const DWELL_DELAY_MS = 1200
const SPORE_INTERVAL_MS = 700
const MAX_SPORES = 6
const SPORE_LIFE_MS = 4200
const OFFSET_FREQUENCY = 8
const NODE_FREQUENCY = 12
const CONTRACTION_RESPONSE = 14
const CONTRACTION_DECAY = 5

interface DampedValue {
  value: number
  velocity: number
}

function clamp(value: number, minimum: number, maximum: number): number {
  const finiteValue = Number.isFinite(value) ? value : 0
  return Math.min(maximum, Math.max(minimum, finiteValue))
}

function criticallyDamped(
  value: number,
  velocity: number,
  target: number,
  frequency: number,
  deltaSeconds: number,
): DampedValue {
  const displacement = value - target
  const decay = Math.exp(-frequency * deltaSeconds)
  const timeAdjustedVelocity = (velocity + frequency * displacement) * deltaSeconds

  return {
    value: target + (displacement + timeAdjustedVelocity) * decay,
    velocity: (velocity - frequency * timeAdjustedVelocity) * decay,
  }
}

function sporeEmissionCount(dwellMs: number): number {
  return Math.max(0, Math.floor((dwellMs - DWELL_DELAY_MS) / SPORE_INTERVAL_MS))
}

function emitSpore(
  state: ParticleSimulationState,
  topology: ParticleTopology,
  emissionIndex: number,
): void {
  if (state.spores.length >= MAX_SPORES || topology.nodes.length === 0) {
    return
  }

  const topologyNode = topology.nodes[(emissionIndex * 17) % topology.nodes.length]
  if (!topologyNode) {
    return
  }

  const radialLength = Math.hypot(topologyNode.homeX, topologyNode.homeY)
  const directionX = radialLength > 0 ? topologyNode.homeX / radialLength : 1
  const directionY = radialLength > 0 ? topologyNode.homeY / radialLength : 0
  const tangentDirection = emissionIndex % 2 === 0 ? 1 : -1
  const outwardSpeed = 0.035 + (emissionIndex % 3) * 0.006
  const tangentSpeed = 0.008 * tangentDirection

  state.spores.push({
    x: topologyNode.homeX * (1 - state.contraction) + state.offsetX,
    y: topologyNode.homeY * (1 - state.contraction) + state.offsetY,
    velocityX: directionX * outwardSpeed - directionY * tangentSpeed,
    velocityY: directionY * outwardSpeed + directionX * tangentSpeed,
    ageMs: 0,
    lifeMs: SPORE_LIFE_MS,
  })
}

export function createSimulationState(
  topology: ParticleTopology,
): ParticleSimulationState {
  return {
    nodes: topology.nodes.map((node) => ({
      x: node.homeX,
      y: node.homeY,
      velocityX: 0,
      velocityY: 0,
    })),
    offsetX: 0,
    offsetY: 0,
    offsetVelocityX: 0,
    offsetVelocityY: 0,
    contraction: 0,
    gentleDwellMs: 0,
    spores: [],
  }
}

export function stepSimulation(
  state: ParticleSimulationState,
  topology: ParticleTopology,
  input: SimulationInput,
  deltaMs: number,
  nowMs: number,
): void {
  const boundedDeltaMs = clamp(deltaMs, 0, 50)
  const deltaSeconds = boundedDeltaMs / 1000
  const pointerX = clamp(input.x, -1, 1)
  const pointerY = clamp(input.y, -1, 1)
  const pointerSpeed = Math.max(0, Number.isFinite(input.speed) ? input.speed : 0)
  const pointerForce = clamp(input.force, 0, 1)
  const pointerDistance = Math.hypot(pointerX, pointerY)
  const pointerIsClose = input.active && pointerDistance < POINTER_REACH

  let retreatTargetX = 0
  let retreatTargetY = 0
  if (pointerIsClose) {
    const distance = Math.max(pointerDistance, 0.0001)
    const proximity = 1 - pointerDistance / POINTER_REACH
    const retreatMagnitude = MAX_RETREAT * proximity * pointerForce
    retreatTargetX = pointerDistance > 0 ? -pointerX / distance * retreatMagnitude : -retreatMagnitude
    retreatTargetY = pointerDistance > 0 ? -pointerY / distance * retreatMagnitude : 0
  }

  const offsetX = criticallyDamped(
    state.offsetX,
    state.offsetVelocityX,
    retreatTargetX,
    OFFSET_FREQUENCY,
    deltaSeconds,
  )
  const offsetY = criticallyDamped(
    state.offsetY,
    state.offsetVelocityY,
    retreatTargetY,
    OFFSET_FREQUENCY,
    deltaSeconds,
  )
  state.offsetX = offsetX.value
  state.offsetY = offsetY.value
  state.offsetVelocityX = offsetX.velocity
  state.offsetVelocityY = offsetY.velocity

  const offsetMagnitude = Math.hypot(state.offsetX, state.offsetY)
  if (offsetMagnitude > MAX_RETREAT) {
    const scale = MAX_RETREAT / offsetMagnitude
    state.offsetX *= scale
    state.offsetY *= scale
    const directionX = state.offsetX / MAX_RETREAT
    const directionY = state.offsetY / MAX_RETREAT
    const outwardVelocity = Math.max(
      0,
      state.offsetVelocityX * directionX + state.offsetVelocityY * directionY,
    )
    state.offsetVelocityX -= outwardVelocity * directionX
    state.offsetVelocityY -= outwardVelocity * directionY
  }

  const contractionTarget = input.active && pointerSpeed >= FAST_SPEED
    ? MAX_CONTRACTION
    : 0
  const contractionRate = contractionTarget > state.contraction
    ? CONTRACTION_RESPONSE
    : CONTRACTION_DECAY
  const contractionBlend = 1 - Math.exp(-contractionRate * deltaSeconds)
  state.contraction += (contractionTarget - state.contraction) * contractionBlend
  state.contraction = clamp(state.contraction, 0, MAX_CONTRACTION)

  const finiteNowMs = Number.isFinite(nowMs) ? nowMs : 0
  const breathTime = finiteNowMs / 1000
  const nodeCount = Math.min(state.nodes.length, topology.nodes.length)
  for (let index = 0; index < nodeCount; index += 1) {
    const node = state.nodes[index]
    const topologyNode = topology.nodes[index]
    if (!node || !topologyNode) {
      continue
    }

    const breath = 1 + Math.sin(breathTime * 1.4 + topologyNode.phase) * 0.022
    const scale = breath * (1 - state.contraction)
    const x = criticallyDamped(
      node.x,
      node.velocityX,
      topologyNode.homeX * scale,
      NODE_FREQUENCY,
      deltaSeconds,
    )
    const y = criticallyDamped(
      node.y,
      node.velocityY,
      topologyNode.homeY * scale,
      NODE_FREQUENCY,
      deltaSeconds,
    )
    node.x = x.value
    node.y = y.value
    node.velocityX = x.velocity
    node.velocityY = y.velocity
  }

  for (const spore of state.spores) {
    spore.x += spore.velocityX * deltaSeconds
    spore.y += spore.velocityY * deltaSeconds
    spore.ageMs += boundedDeltaMs
  }
  state.spores = state.spores.filter((spore) => spore.ageMs < spore.lifeMs)

  const isGentleDwell = pointerIsClose && pointerSpeed <= GENTLE_SPEED
  if (!isGentleDwell) {
    state.gentleDwellMs = 0
    return
  }

  const previousEmissionCount = sporeEmissionCount(state.gentleDwellMs)
  state.gentleDwellMs += boundedDeltaMs
  const nextEmissionCount = sporeEmissionCount(state.gentleDwellMs)
  for (
    let emissionIndex = previousEmissionCount + 1;
    emissionIndex <= nextEmissionCount;
    emissionIndex += 1
  ) {
    emitSpore(state, topology, emissionIndex)
  }
}
